import React from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik';
import Typesense from 'typesense';
import { ColorField, DropdownField, TextAreaField, TextField } from './settings-form/elements';
import {
  filterSourceDefaults,
  lineColor,
  groupDefaults,
  colorPalette
} from './settings-form/default-values';

const sourcesSchema = Yup.object().shape({
  title: Yup.string().required(`Can't be blank`),
  defaultIndex: Yup.string().required(`Can't be blank`),
  style: Yup.string()
    .oneOf(['default', 'alt'])
    .required(`Can't be blank`),
  lineStyle: Yup.string()
    .oneOf(['linear', 'monotone'])
    .required(`Can't be blank`),
  groups: Yup.array().of(
    Yup.object({
      label: Yup.string().required(`Can't be blank`),
      labelFields: Yup.string().required(`Can't be blank`),
      description: Yup.string().required(`Can't be blank`)
    }),
  ).min(1, 'At least one group is required').max(5, '10 groups is maximum.')
});

function cartesian(arrays) {
  return arrays.reduce((acc, curr) => {
    const res = [];
    acc.forEach(a => {
      curr.forEach(b => {
        res.push([...a, b]);
      });
    });
    return res;
  }, [[]]);
}

async function DoSearch(host, apiKey, indexName, config) {
  const client = new Typesense.Client({
    nodes: [
      {host: host, port: 443, protocol: 'https'}
    ],
    apiKey: apiKey,
    connectionTimeoutSeconds: 2,
  });

  const {groupByFields} = config;

  const searchParameters = {
    q: "*",
    query_by: "*",
    facet_by: config.groupByFields,
    max_facet_values: 50,
    filter_by: config.filterBy,
  };

  const result = await client.collections(indexName).documents().search(searchParameters);

  const facetCounts = {};
  groupByFields.split(',').forEach(g => facetCounts[g] = []);

  for (const facet of result.facet_counts) {
    const {field_name, counts} = facet;
    facetCounts[field_name] = counts.map(f => f.value.toLowerCase());
  }

  const arrays = Object.values(facetCounts);

  const combinations = cartesian(arrays).map(combo => combo.join("|"));

  return combinations;
}

function generateColors() {
  const colors = [];
  for (let i = 0; i < 5; i++) {
    for (const item of Object.values(colorPalette)) {
      colors.push(item);
    }
  }
  return colors;
}

export default function NvChartSettingsForm({submitHandler, defaultValues}) {

  function onClickHandler(values, groupIndex, evt, form) {
    const {defaultIndex} = values;

    evt.target.classList.add('opacity-75');
    evt.target.disabled = true;

    const tsHostElement = document.querySelector('meta[name="typesense-host"]');
    const tsKeyElement = document.querySelector('meta[name="typesense-key"]');
    const colors = generateColors();

    DoSearch(tsHostElement.content, tsKeyElement.content, defaultIndex, values.groups[groupIndex])
      .then(combinations => {
        const newColorsMap = combinations.map(c => {
          return {id: c, color: colors.shift()}
        });
        form.setFieldValue(`groups.${groupIndex}.colorsMap`, newColorsMap);
        evt.target.classList.remove('opacity-75');
      })
      .catch()
      .finally(_ => {
        evt.target.disabled = false;
        evt.target.classList.remove('opacity-75');
      });
  }

  return (
    <div className='nv-chart-settings-form'>
      <Formik initialValues={defaultValues} validationSchema={sourcesSchema} onSubmit={values => submitHandler(values)}>
        {({values}) => {
          return (
            <Form>
              <div className='nv-chart-settings-form__fields'>

                {/* Basic settings */}
                <div className='nv-chart-settings-form__section'>
                  <details>
                    <summary>
                      Basic settings
                    </summary>
                    <div>
                      <div className='nv-chart-settings-form__field'>
                        <TextField name='title' label='Title'/>
                      </div>
                      <div className='nv-chart-settings-form__field'>
                        <TextField name='defaultIndex' label='Collection ID'/>
                        <p className='p--small text-light pt-1'>Typesense collection ID</p>
                      </div>
                      <div className='nv-chart-settings-form__field'>
                        <label className='form-label' htmlFor='type'>Style</label>
                        <Field as='select' name='style' className='form-control form-control-sm'>
                          <option value='default'>Default</option>
                          <option value='alt'>Alt</option>
                        </Field>
                        <ErrorMessage name='style' component='p' className='field-error mt-2 mb-0'/>
                      </div>
                      <div className='nv-chart-settings-form__field'>
                        <label className='form-label' htmlFor='type'>Line Style</label>
                        <Field as='select' name='lineStyle' className='form-control form-control-sm'>
                          <option value=''>Select</option>
                          <option value='monotone'>Smooth</option>
                          <option value='linear'>Linear</option>
                        </Field>
                        <ErrorMessage name='lineStyle' component='p' className='field-error mt-2 mb-0'/>
                      </div>
                    </div>
                  </details>
                </div>

                {/* Groups */}
                <div className='nv-chart-settings-form__groups'>
                  <FieldArray name='groups'>
                    {({remove, push, form}) => {
                      return (
                        <div className='group'>
                          {values.groups && values.groups.length > 0 &&
                            values.groups.map((entry, idx) => {
                              return (
                                <div className='nv-chart-settings-form__section group-settings' key={idx}>
                                  <details>
                                    <summary>
                                      Group {idx + 1}
                                    </summary>
                                    <div>
                                      <div className='row'>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].label`} label='Label'/>
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].description`} label='Description'/>
                                        </div>
                                        <div className='col-4'>
                                          <DropdownField
                                            name={`groups[${idx}].type`}
                                            label='Chart Type'
                                            options={[
                                              ['line', 'Line']
                                            ]}
                                          />
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].groupByFields`} label='GroupBy fields'
                                                     placeholder='Typesense field name'/>
                                          <p className="p--small text-light pt-1">Comma separated list of fields.</p>
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].groupLabelFormat`} label='Group Label Format'
                                                     placeholder='Typesense field name'/>
                                          <p
                                            className="p--small text-light pt-1">Example: {`{{hw}} ({{framework}}, {{precision}})`}</p>
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].labelFields`} label='Chart Title'/>
                                          <p className="p--small text-light pt-1">Comma separated list of fields.</p>
                                        </div>
                                      </div>

                                      <hr/>

                                      <div className='row'>
                                        <FieldArray name={`groups[${idx}].filters`}>
                                          {({push: pushAllowed, remove: removeAllowed}) => {
                                            const items = form.values.groups[idx].filters;
                                            return (
                                              <div className='group'>
                                                <h3 className='h--smallest text-white'>Filters</h3>
                                                <div className='col-12'>
                                                  <TextField name={`groups[${idx}].filterBy`} label='Filter by'/>
                                                  <p className="p--small text-light pt-1">For details, see <a
                                                    href="https://typesense.org/docs/26.0/api/search.html#filter-parameters"
                                                    target="_blank">Typesense documentation</a>.</p>
                                                  <p className="p--small text-light pt-1">Example: <code>hw:=[`H200`,`H100`,`GB200`]
                                                    && precision:=[`FP4`]</code></p>
                                                </div>

                                                {items && items.length > 0 && items.map((item, gIdx) => {
                                                  return (
                                                    <div key={item.id} className='row'>
                                                      <div className="col-4">
                                                        <TextField name={`groups[${idx}].filters.${gIdx}.attribute`}
                                                                   label='Attribute'
                                                                   placeholder='Typesense field name'/>
                                                      </div>
                                                      <div className="col-4">
                                                        <TextField name={`groups[${idx}].filters.${gIdx}.label`}
                                                                   label='Label'/>
                                                      </div>
                                                      <div className="col-3">
                                                        <DropdownField
                                                          name={`groups[${idx}].filters.${gIdx}.kind`}
                                                          label='Widget'
                                                          options={[
                                                            ['checkboxes', 'Checkboxes'],
                                                            ['radio', 'Radio'],
                                                          ]}
                                                        />
                                                      </div>
                                                      <div className="col-1 align-self-end my-2">
                                                        <button type="button" className="btn btn-danger btn-sm"
                                                                onClick={() => removeAllowed(gIdx)}>
                                                          ×
                                                        </button>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-primary btn-sm"
                                                  onClick={() =>
                                                    pushAllowed({
                                                      ...filterSourceDefaults,
                                                      id: crypto.randomUUID(),
                                                    })
                                                  }
                                                >
                                                  + Add Filter
                                                </button>
                                              </div>);
                                          }}
                                        </FieldArray>
                                      </div>

                                      <hr/>

                                      {/* X Axis */}
                                      <div className='row'>
                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">X Axis</h3>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.x.label`} label='Label'/>
                                          <p className="p--small text-light pt-1">X axis label</p>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.x.field`} label='Field'/>
                                          <p className="p--small text-light pt-1">Typesense field</p>
                                        </div>
                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">Y Axis</h3>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.y.label`} label='Label'/>
                                          <p className="p--small text-light pt-1">Y axis label</p>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.y.field`} label='Field'/>
                                          <p className="p--small text-light pt-1">Typesense field</p>
                                        </div>
                                      </div>

                                      <hr/>

                                      {/* Tooltip */}
                                      <div className='row'>
                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">Tooltip</h3>
                                        </div>
                                        {/*Tooltip*/}
                                        <div className='col-12'>
                                          <TextField name={`groups[${idx}].tooltip.title`} label='Title'/>
                                        </div>
                                        <div className='col-12'>
                                          <TextAreaField name={`groups[${idx}].tooltip.text`} label='Text'/>
                                          <p className="p--small text-light pt-1">Use {`{{fieldname}}`} as
                                            placeholder.</p>
                                        </div>
                                      </div>

                                      <hr/>

                                      <div className='row'>
                                        <div className='col-12'>
                                          <button className='btn btn-outline-warning' type='button'
                                                  onClick={(evt) => onClickHandler(values, idx, evt, form)}>Generate
                                          </button>
                                          <p className="p--small text-light pt-2">This action cannot be undone. After
                                            generation, all colors will be overwritten.</p>
                                        </div>
                                        <FieldArray name={`groups[${idx}].colorsMap`}>
                                          {({push: pushLineColor, remove: removeLineColor}) => {
                                            const items = form.values.groups[idx].colorsMap;
                                            return (
                                              <div className='group'>
                                                {items && items.length > 0 && items.map((item, gIdx) => {
                                                  return (
                                                    <div key={item.id} className='row'>
                                                      <div className="col-6">
                                                        <TextField name={`groups[${idx}].colorsMap.${gIdx}.id`} label=''
                                                                   placeholder='ID' classes={['my-2 d-flex flex-row']}/>
                                                      </div>
                                                      <div className="col-4">
                                                        <TextField name={`groups[${idx}].colorsMap.${gIdx}.color`}
                                                                   label='' placeholder='Color'
                                                                   classes={['my-2 d-flex flex-row']}/>
                                                      </div>
                                                      <div className='col-1 d-flex'>
                                                        <ColorField name={`groups[${idx}].colorsMap.${gIdx}.color`}
                                                                    label='' placeholder='Color'
                                                                    classes={['my-2 d-flex flex-row']}/>
                                                      </div>
                                                      <div className="col-1 align-self-end my-2">
                                                        <button type="button" className="btn btn-danger btn-sm"
                                                                onClick={() => removeLineColor(gIdx)}>
                                                          ×
                                                        </button>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-primary btn-sm"
                                                  onClick={() =>
                                                    pushLineColor({
                                                      ...lineColor,
                                                      id: crypto.randomUUID(),
                                                    })
                                                  }
                                                >
                                                  + Add Color
                                                </button>
                                              </div>);
                                          }}
                                        </FieldArray>
                                      </div>

                                      <div className="row">
                                        <div className='col-12 my-2 pt-4'>
                                          <button type='button' className='btn btn-outline-warning btn-sm'
                                                  onClick={() => remove(idx)}>
                                            Remove Group
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </details>
                                </div>
                              );
                            })}
                          <button type='button' className='btn btn-outline-light btn-sm'
                                  onClick={() => push({...groupDefaults})}>
                            Add Group
                          </button>
                        </div>
                      );
                    }}
                  </FieldArray>
                </div>
              </div>
              <div className='nv-chart-settings-form__actions'>
                <button type='submit' className='btn btn-info btn-sm'>Save settings</button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
